import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy, Award, Zap, Target, Crown, Star } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";

const ACHIEVEMENT_DEFINITIONS = [
  { id: 'first_challenge', name: 'First Steps', description: 'Complete your first challenge', points: 100, icon: Star, tier: 'bronze' },
  { id: 'week_warrior', name: 'Week Warrior', description: 'Complete a 7-day streak', points: 200, icon: Zap, tier: 'bronze' },
  { id: 'month_master', name: 'Month Master', description: 'Complete a 30-day streak', points: 500, icon: Trophy, tier: 'silver' },
  { id: 'perfect_week', name: 'Perfect Week', description: '100% completion for 7 days', points: 300, icon: Award, tier: 'silver' },
  { id: 'community_champion', name: 'Community Champion', description: 'Help 10 partners reach their goals', points: 400, icon: Crown, tier: 'gold' },
  { id: 'consistency_king', name: 'Consistency King', description: '90% success rate over 30 days', points: 600, icon: Target, tier: 'gold' },
];

export function GamificationDashboard() {
  const { data: user } = useQuery({
    queryKey: ['user'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const { data: userAchievements } = useQuery({
    queryKey: ['user-achievements', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('user_achievements')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  const { data: totalPoints } = useQuery({
    queryKey: ['user-points-total', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { data, error } = await supabase
        .from('user_points')
        .select('points')
        .eq('user_id', user.id);

      if (error) throw error;
      return data.reduce((sum, p) => sum + p.points, 0);
    },
    enabled: !!user?.id,
  });

  const { data: leaderboard } = useQuery({
    queryKey: ['points-leaderboard'],
    queryFn: async () => {
      // Manual aggregation of points
      const { data: points, error: pointsError } = await supabase
        .from('user_points')
        .select('user_id, points');

      if (pointsError) throw pointsError;

      const aggregated = points?.reduce((acc, p) => {
        acc[p.user_id] = (acc[p.user_id] || 0) + p.points;
        return acc;
      }, {} as Record<string, number>) || {};

      const sorted = Object.entries(aggregated)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 10);

      const userIds = sorted.map(([userId]) => userId);
      if (userIds.length === 0) return [];

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      return sorted.map(([userId, points], index) => ({
        user_id: userId,
        total_points: points,
        rank: index + 1,
        display_name: profileMap.get(userId)?.display_name || 'User',
      }));
    },
  });

  const earnedIds = new Set(userAchievements?.map(a => a.achievement_id) || []);
  const earnedAchievements = ACHIEVEMENT_DEFINITIONS.filter(a => earnedIds.has(a.id));
  const availableAchievements = ACHIEVEMENT_DEFINITIONS.filter(a => !earnedIds.has(a.id));

  const nextLevelPoints = Math.ceil((totalPoints || 0) / 1000) * 1000 + 1000;
  const progressToNextLevel = ((totalPoints || 0) % 1000) / 10;

  return (
    <div className="space-y-6">
      {/* Points & Level */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-accent" />
            Your Progress
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-3xl font-bold text-primary">{totalPoints || 0}</p>
              <p className="text-sm text-muted-foreground">Total Points</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-semibold">Level {Math.floor((totalPoints || 0) / 1000) + 1}</p>
              <p className="text-xs text-muted-foreground">{nextLevelPoints - (totalPoints || 0)} to next level</p>
            </div>
          </div>
          <div className="space-y-2">
            <Progress value={progressToNextLevel} className="h-2" />
            <p className="text-xs text-muted-foreground text-center">
              {progressToNextLevel.toFixed(0)}% to Level {Math.floor((totalPoints || 0) / 1000) + 2}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Earned Achievements */}
      {earnedAchievements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Achievements ({earnedAchievements.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {earnedAchievements.map((achievement) => {
                const Icon = achievement.icon;
                return (
                  <div
                    key={achievement.id}
                    className="p-4 border border-primary/20 rounded-lg bg-primary/5 text-center space-y-2"
                  >
                    <Icon className="h-8 w-8 mx-auto text-primary" />
                    <p className="font-medium text-sm">{achievement.name}</p>
                    <Badge variant="secondary" className="text-xs">+{achievement.points}pts</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Achievements */}
      {availableAchievements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-secondary" />
              Available Achievements
            </CardTitle>
            <CardDescription>Complete these to earn more points</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {availableAchievements.slice(0, 5).map((achievement) => {
                const Icon = achievement.icon;
                return (
                  <div
                    key={achievement.id}
                    className="flex items-center gap-3 p-3 border rounded-lg opacity-60"
                  >
                    <div className="p-2 bg-muted rounded-lg">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">{achievement.name}</p>
                      <p className="text-xs text-muted-foreground">{achievement.description}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">+{achievement.points}pts</Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-accent" />
            Leaderboard
          </CardTitle>
          <CardDescription>Top performers this month</CardDescription>
        </CardHeader>
        <CardContent>
          {leaderboard && leaderboard.length > 0 ? (
            <div className="space-y-2">
              {leaderboard.map((entry: any, index: number) => {
                const isMe = entry.user_id === user?.id;
                return (
                  <div
                    key={entry.user_id}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      isMe ? 'bg-primary/10 border border-primary/20' : 'bg-muted/50'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-primary text-primary-foreground' :
                      index === 1 ? 'bg-secondary text-background' :
                      index === 2 ? 'bg-accent text-background' :
                      'bg-muted'
                    }`}>
                      {index + 1}
                    </div>
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>
                        {entry.display_name?.[0] || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <p className="font-medium text-sm">
                        {isMe ? 'You' : entry.display_name || 'User'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary">{entry.total_points || 0}</p>
                      <p className="text-xs text-muted-foreground">points</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Crown className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
              <p className="text-sm text-muted-foreground">
                No leaderboard data yet. Complete challenges to earn points!
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
